import { Button, Col, Collapse, Divider, Image, Row, Spin, Table } from 'antd';
import PropTypes from 'prop-types';
import { useEffect, Fragment, useMemo, useCallback } from 'react';
import withStyles from 'react-jss';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import { EditOutlined, LoadingOutlined } from '@ant-design/icons';
import { useHistory } from 'react-router';

import { clearJobchartPreview, getJobchartPreview } from '../Actions';
import Breadcrumbs from '../Components/Breadcrumbs';
import LeftGrid from '../Components/LeftGrid';
import RightGrid from '../Components/RightGrid';
import styles from '../styles';
import { apiURL } from '../config';
import constants from '../constants';
import { get } from '../utils';

const antIcon = <LoadingOutlined style={{ fontSize: 24 }} spin />;

const PreviewJobchart = props => {
  const { classes, match } = props;

  const dispatch = useDispatch();
  const history = useHistory();

  const jobchart = useSelector(state => state.jobchartPreview);

  const auth = useSelector(state => state.auth);
  const permissions = useMemo(() => get(auth, ['permissions'], []), [auth]);

  useEffect(() => {
    dispatch(getJobchartPreview(match.params.id));
    return () => {
      dispatch(clearJobchartPreview());
    };
  }, []);

  const rowKey = useCallback(
    record =>
      `Id${record.id}P${record.product}F${record.fabric}W${record.width}B${
        record.baseColor
      }P${record.printColor || ''}` ||
      `Id${record.id}P${record.product}A${record.accessory}` ||
      `Id${record.id}P${record.product}S${record.name}`,
    []
  );

  const stylesSummaryColumns = useMemo(() => {
    const _columns = [
      {
        title: 'Product',
        dataIndex: 'product',
      },
      {
        title: 'Style',
        dataIndex: 'name',
      },
      {
        title: 'Description',
        dataIndex: 'description',
      },
      {
        title: 'Size',
        dataIndex: 'size',
      },
      {
        title: 'Color',
        children: [
          {
            title: 'Base',
            dataIndex: 'baseColor',
          },
          {
            title: 'Print',
            dataIndex: 'printColor',
          },
          {
            title: 'Embroidery',
            dataIndex: 'embroideryColor',
          },
          {
            title: 'Acid Wash',
            dataIndex: 'acidWashColor',
          },
        ],
      },
      {
        title: 'Total Qty',
        dataIndex: 'totalQuantity',
      },
    ];

    return _columns;
  }, []);

  const fabricsSummaryColumns = useMemo(() => {
    const _columns = [
      {
        title: 'Product',
        dataIndex: 'product',
      },
      {
        title: 'Fabric',
        dataIndex: 'fabric',
      },
      {
        title: 'Width',
        dataIndex: 'width',
      },
      {
        title: 'Color',
        children: [
          {
            title: 'Base',
            dataIndex: 'baseColor',
          },
          {
            title: 'Print',
            dataIndex: 'printColor',
          },
        ],
      },
      {
        title: 'Total Qty',
        dataIndex: 'totalQuantity',
      },
      {
        title: 'Stock',
        dataIndex: 'stock',
      },
      {
        title: 'To Order',
        dataIndex: 'toOrder',
      },
    ];

    return _columns;
  }, []);

  const fabricsSummaryWithoutProductColumns = useMemo(() => {
    const _columns = [
      {
        title: 'Fabric',
        dataIndex: 'fabric',
      },
      {
        title: 'Width',
        dataIndex: 'width',
      },
      {
        title: 'Color',
        children: [
          {
            title: 'Base',
            dataIndex: 'baseColor',
          },
          {
            title: 'Print',
            dataIndex: 'printColor',
          },
        ],
      },
      {
        title: 'Total Qty',
        dataIndex: 'totalQuantity',
      },
      {
        title: 'Stock',
        dataIndex: 'stock',
      },
      {
        title: 'To Order',
        dataIndex: 'toOrder',
      },
    ];

    return _columns;
  }, []);

  const accessoriesSummaryColumns = useMemo(() => {
    const _columns = [
      {
        title: 'Product',
        dataIndex: 'product',
      },
      {
        title: 'Accessory',
        dataIndex: 'accessory',
      },
      {
        title: 'Unit',
        dataIndex: 'unit',
      },
      {
        title: 'Total Qty',
        dataIndex: 'totalQuantity',
      },
      {
        title: 'Stock',
        dataIndex: 'stock',
      },
      {
        title: 'To Order',
        dataIndex: 'toOrder',
      },
    ];

    return _columns;
  }, []);

  const renderExistingImage = (image, index) => {
    return (
      <Fragment key={index}>
        <Col xs={24} sm={7} md={5} lg={3} xl={2}>
          <Image
            src={`${apiURL}/${image.key}`}
            className={classes.jobchartImage}
          />
        </Col>
        <Col xs={0} sm={1} />
      </Fragment>
    );
  };

  const renderProduct = (
    product,
    productIndex,
    totalProducts,
    toCompareIndex = null
  ) => {
    return (
      <Fragment key={productIndex}>
        <Row>
          <Col xs={12}>
            <span className={classes.previewSpan}>Product: </span>
            <span
              style={{
                color:
                  jobchart.oldVersions[toCompareIndex] !== undefined &&
                  jobchart.oldVersions[toCompareIndex].products?.[productIndex]
                    ?.product !== product.product
                    ? 'Red'
                    : 'Black',
              }}
            >
              {product.product}
            </span>
          </Col>
          <Col xs={12}>
            <span className={classes.previewSpan}>Remark: </span>
            <span
              style={{
                color:
                  jobchart.oldVersions[toCompareIndex] !== undefined &&
                  jobchart.oldVersions[toCompareIndex].products?.[productIndex]
                    ?.remark !== product.remark
                    ? 'Red'
                    : 'Black',
              }}
            >
              {product.remark}
            </span>
          </Col>
        </Row>
        {product.files && product.files.length ? (
          <>
            <br />
            <Row>
              <Image.PreviewGroup>
                {product.files.map(renderExistingImage)}
              </Image.PreviewGroup>
            </Row>
          </>
        ) : null}
        {product.styles.map((style, styleIndex) => {
          return (
            <Fragment key={styleIndex}>
              <Divider orientation="left" plain>
                Style {styleIndex + 1} &nbsp;&nbsp;
              </Divider>
              <Row>
                <Col
                  xs={24}
                  sm={8}
                  md={6}
                  lg={4}
                  className={classes.paddingSmall}
                >
                  <span className={classes.previewSpan}>Style: </span>
                  <span
                    style={{
                      color:
                        jobchart.oldVersions[toCompareIndex] !== undefined &&
                        jobchart.oldVersions[toCompareIndex].products?.[
                          productIndex
                        ]?.styles?.[styleIndex]?.name !== style.name
                          ? 'Red'
                          : 'Black',
                    }}
                  >
                    {style.name}
                  </span>
                </Col>
                <Col
                  xs={24}
                  sm={8}
                  md={6}
                  lg={4}
                  className={classes.paddingSmall}
                >
                  <span className={classes.previewSpan}>Description: </span>
                  <span
                    style={{
                      color:
                        jobchart.oldVersions[toCompareIndex] !== undefined &&
                        jobchart.oldVersions[toCompareIndex].products?.[
                          productIndex
                        ]?.styles?.[styleIndex]?.name !== style.name
                          ? 'Red'
                          : 'Black',
                    }}
                  >
                    {style.description}
                  </span>
                </Col>
                <Col
                  xs={24}
                  sm={8}
                  md={6}
                  lg={4}
                  className={classes.paddingSmall}
                >
                  <span className={classes.previewSpan}>Size: </span>
                  <span
                    style={{
                      color:
                        jobchart.oldVersions[toCompareIndex] !== undefined &&
                        jobchart.oldVersions[toCompareIndex].products?.[
                          productIndex
                        ]?.styles?.[styleIndex]?.size !== style.size
                          ? 'Red'
                          : 'Black',
                    }}
                  >
                    {style.size} {style.unit}
                  </span>
                </Col>
                <Col
                  xs={24}
                  sm={8}
                  md={6}
                  lg={4}
                  className={classes.paddingSmall}
                >
                  <span className={classes.previewSpan}>Base Color: </span>
                  <span
                    style={{
                      color:
                        jobchart.oldVersions[toCompareIndex] !== undefined &&
                        jobchart.oldVersions[toCompareIndex].products?.[
                          productIndex
                        ]?.styles?.[styleIndex]?.baseColor !== style.baseColor
                          ? 'Red'
                          : 'Black',
                    }}
                  >
                    {style.baseColor}
                  </span>
                </Col>
                <Col
                  xs={24}
                  sm={8}
                  md={6}
                  lg={4}
                  className={classes.paddingSmall}
                >
                  <span className={classes.previewSpan}>Print Color:</span>
                  &nbsp;
                  <span
                    style={{
                      color:
                        jobchart.oldVersions[toCompareIndex] !== undefined &&
                        jobchart.oldVersions[toCompareIndex].products?.[
                          productIndex
                        ]?.styles?.[styleIndex]?.printColor !== style.printColor
                          ? 'Red'
                          : 'Black',
                    }}
                  >
                    {style.printColor}
                  </span>
                </Col>
                <Col
                  xs={24}
                  sm={8}
                  md={6}
                  lg={4}
                  className={classes.paddingSmall}
                >
                  <span className={classes.previewSpan}>Embroidery Color:</span>
                  &nbsp;
                  <span
                    style={{
                      color:
                        jobchart.oldVersions[toCompareIndex] !== undefined &&
                        jobchart.oldVersions[toCompareIndex].products?.[
                          productIndex
                        ]?.styles?.[styleIndex]?.embroideryColor !==
                          style.embroideryColor
                          ? 'Red'
                          : 'Black',
                    }}
                  >
                    {style.embroideryColor}
                  </span>
                </Col>
                <Col
                  xs={24}
                  sm={8}
                  md={6}
                  lg={4}
                  className={classes.paddingSmall}
                >
                  <span className={classes.previewSpan}>Acid Wash Color:</span>
                  &nbsp;
                  <span
                    style={{
                      color:
                        jobchart.oldVersions[toCompareIndex] !== undefined &&
                        jobchart.oldVersions[toCompareIndex].products?.[
                          productIndex
                        ]?.styles?.[styleIndex]?.acidWashColor !==
                          style.acidWashColor
                          ? 'Red'
                          : 'Black',
                    }}
                  >
                    {style.acidWashColor}
                  </span>
                </Col>
                <Col
                  xs={24}
                  sm={8}
                  md={6}
                  lg={4}
                  className={classes.paddingSmall}
                >
                  <span className={classes.previewSpan}>Quantity: </span>
                  <span
                    style={{
                      color:
                        jobchart.oldVersions[toCompareIndex] !== undefined &&
                        jobchart.oldVersions[toCompareIndex].products?.[
                          productIndex
                        ]?.styles?.[styleIndex]?.quantity !== style.quantity
                          ? 'Red'
                          : 'Black',
                    }}
                  >
                    {style.quantity}
                  </span>
                </Col>
                <Col
                  xs={24}
                  sm={8}
                  md={6}
                  lg={4}
                  className={classes.paddingSmall}
                >
                  <span className={classes.previewSpan}>Additional: </span>
                  <span
                    style={{
                      color:
                        jobchart.oldVersions[toCompareIndex] !== undefined &&
                        jobchart.oldVersions[toCompareIndex].products?.[
                          productIndex
                        ]?.styles?.[styleIndex]?.additional !== style.additional
                          ? 'Red'
                          : 'Black',
                    }}
                  >
                    {style.additional}
                  </span>
                </Col>
                <Col
                  xs={24}
                  sm={8}
                  md={6}
                  lg={4}
                  className={classes.paddingSmall}
                >
                  <span className={classes.previewSpan}>Total: </span>
                  <span
                    style={{
                      color:
                        jobchart.oldVersions[toCompareIndex] !== undefined &&
                        jobchart.oldVersions[toCompareIndex].products?.[
                          productIndex
                        ]?.styles?.[styleIndex]?.totalQuantity !==
                          style.totalQuantity
                          ? 'Red'
                          : 'Black',
                    }}
                  >
                    {style.totalQuantity}
                  </span>
                </Col>
                <Col
                  xs={24}
                  sm={8}
                  md={6}
                  lg={4}
                  className={classes.paddingSmall}
                >
                  <span className={classes.previewSpan}>
                    Inhouse Processes:
                  </span>
                  <span
                  // style={{
                  //   color:
                  //     jobchart.oldVersions[toCompareIndex] !== undefined &&
                  //     jobchart.oldVersions[toCompareIndex].products?.[
                  //       productIndex
                  //     ]?.styles?.[styleIndex]?.totalQuantity !==
                  //       style.totalQuantity
                  //       ? 'Red'
                  //       : 'Black',
                  // }}
                  >
                    {(style.inhouseProcesses || []).join(', ')}
                  </span>
                </Col>
              </Row>
              <br />
              <Row>
                <table className={classes.inputTable}>
                  <thead className={classes.inputThead}>
                    <tr>
                      <th className={classes.inputTh}>Fabric</th>
                      <th className={classes.inputTh} style={{ width: '10%' }}>
                        Width
                      </th>
                      <th className={classes.inputTh} style={{ width: '10%' }}>
                        Base Color
                      </th>
                      <th className={classes.inputTh} style={{ width: '10%' }}>
                        Print Color
                      </th>
                      <th className={classes.inputTh} style={{ width: '8%' }}>
                        Consumption
                      </th>
                      <th className={classes.inputTh} style={{ width: '8%' }}>
                        Additional %
                      </th>
                      <th className={classes.inputTh} style={{ width: '8%' }}>
                        Total Qty
                      </th>
                      <th className={classes.inputTh} style={{ width: '8%' }}>
                        Stock
                      </th>
                      <th className={classes.inputTh} style={{ width: '8%' }}>
                        To Order
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {style.fabrics.map((fabric, fabricIndex) => (
                      <tr key={fabricIndex}>
                        <td className={classes.inputTdWithPadding}>
                          <span
                            style={{
                              color:
                                jobchart.oldVersions[toCompareIndex] !==
                                  undefined &&
                                jobchart.oldVersions[toCompareIndex].products?.[
                                  productIndex
                                ]?.styles?.[styleIndex]?.fabrics?.[fabricIndex]
                                  ?.fabric !== fabric.fabric
                                  ? 'Red'
                                  : 'Black',
                            }}
                          >
                            {fabric.fabric}
                          </span>
                        </td>
                        <td className={classes.inputTdWithPadding}>
                          <span
                            style={{
                              color:
                                jobchart.oldVersions[toCompareIndex] !==
                                  undefined &&
                                jobchart.oldVersions[toCompareIndex].products?.[
                                  productIndex
                                ]?.styles?.[styleIndex]?.fabrics?.[fabricIndex]
                                  ?.width !== fabric.width
                                  ? 'Red'
                                  : 'Black',
                            }}
                          >
                            {fabric.width}
                          </span>
                        </td>
                        <td className={classes.inputTdWithPadding}>
                          <span
                            style={{
                              color:
                                jobchart.oldVersions[toCompareIndex] !==
                                  undefined &&
                                jobchart.oldVersions[toCompareIndex].products?.[
                                  productIndex
                                ]?.styles?.[styleIndex]?.fabrics?.[fabricIndex]
                                  ?.baseColor !== fabric.baseColor
                                  ? 'Red'
                                  : 'Black',
                            }}
                          >
                            {fabric.baseColor}
                          </span>
                        </td>
                        <td className={classes.inputTdWithPadding}>
                          <span
                            style={{
                              color:
                                jobchart.oldVersions[toCompareIndex] !==
                                  undefined &&
                                jobchart.oldVersions[toCompareIndex].products?.[
                                  productIndex
                                ]?.styles?.[styleIndex]?.fabrics?.[fabricIndex]
                                  ?.printColor !== fabric.printColor
                                  ? 'Red'
                                  : 'Black',
                            }}
                          >
                            {fabric.printColor}
                          </span>
                        </td>
                        <td className={classes.inputTdWithPadding}>
                          <span
                            style={{
                              color:
                                jobchart.oldVersions[toCompareIndex] !==
                                  undefined &&
                                jobchart.oldVersions[toCompareIndex].products?.[
                                  productIndex
                                ]?.styles?.[styleIndex]?.fabrics?.[fabricIndex]
                                  ?.consumption !== fabric.consumption
                                  ? 'Red'
                                  : 'Black',
                            }}
                          >
                            {fabric.consumption}
                          </span>
                        </td>
                        <td className={classes.inputTdWithPadding}>
                          <span
                            style={{
                              color:
                                jobchart.oldVersions[toCompareIndex] !==
                                  undefined &&
                                jobchart.oldVersions[toCompareIndex].products?.[
                                  productIndex
                                ]?.styles?.[styleIndex]?.fabrics?.[fabricIndex]
                                  ?.additionalPercentage !==
                                  fabric.additionalPercentage
                                  ? 'Red'
                                  : 'Black',
                            }}
                          >
                            {fabric.additionalPercentage}
                          </span>
                        </td>
                        <td className={classes.inputTdWithPadding}>
                          <span
                            style={{
                              color:
                                jobchart.oldVersions[toCompareIndex] !==
                                  undefined &&
                                jobchart.oldVersions[toCompareIndex].products?.[
                                  productIndex
                                ]?.styles?.[styleIndex]?.fabrics?.[fabricIndex]
                                  ?.totalQuantity !== fabric.totalQuantity
                                  ? 'Red'
                                  : 'Black',
                            }}
                          >
                            {fabric.totalQuantity}
                          </span>
                        </td>
                        <td className={classes.inputTdWithPadding}>
                          <span
                            style={{
                              color:
                                jobchart.oldVersions[toCompareIndex] !==
                                  undefined &&
                                jobchart.oldVersions[toCompareIndex].products?.[
                                  productIndex
                                ]?.styles?.[styleIndex]?.fabrics?.[fabricIndex]
                                  ?.stock !== fabric.stock
                                  ? 'Red'
                                  : 'Black',
                            }}
                          >
                            {fabric.stock}
                          </span>
                        </td>
                        <td className={classes.inputTdWithPadding}>
                          <span
                            style={{
                              color:
                                jobchart.oldVersions[toCompareIndex] !==
                                  undefined &&
                                jobchart.oldVersions[toCompareIndex].products?.[
                                  productIndex
                                ]?.styles?.[styleIndex]?.fabrics?.[fabricIndex]
                                  ?.toOrder !== fabric.toOrder
                                  ? 'Red'
                                  : 'Black',
                            }}
                          >
                            {fabric.toOrder}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </Row>
              {style.accessories && style.accessories.length ? (
                <>
                  <br />
                  <Row>
                    <table className={classes.inputTable}>
                      <thead className={classes.inputThead}>
                        <tr>
                          <th className={classes.inputTh}>Accessory</th>
                          <th
                            className={classes.inputTh}
                            style={{ width: '6%' }}
                          >
                            Unit
                          </th>
                          <th
                            className={classes.inputTh}
                            style={{ width: '8%' }}
                          >
                            For Process
                          </th>
                          <th
                            className={classes.inputTh}
                            style={{ width: '8%' }}
                          >
                            Consumption
                          </th>
                          <th
                            className={classes.inputTh}
                            style={{ width: '8%' }}
                          >
                            Additional %
                          </th>
                          <th
                            className={classes.inputTh}
                            style={{ width: '8%' }}
                          >
                            Total Qty
                          </th>
                          <th
                            className={classes.inputTh}
                            style={{ width: '8%' }}
                          >
                            Stock
                          </th>
                          <th
                            className={classes.inputTh}
                            style={{ width: '8%' }}
                          >
                            To Order
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {style.accessories.map((accessory, accessoryIndex) => (
                          <tr key={accessoryIndex}>
                            <td className={classes.inputTdWithPadding}>
                              <span
                                style={{
                                  color:
                                    jobchart.oldVersions[toCompareIndex] !==
                                      undefined &&
                                    jobchart.oldVersions[toCompareIndex]
                                      .products?.[productIndex]?.styles?.[
                                      styleIndex
                                    ]?.accessories?.[accessoryIndex]
                                      ?.accessory !== accessory.accessory
                                      ? 'Red'
                                      : 'Black',
                                }}
                              >
                                {accessory.accessory}
                              </span>
                            </td>
                            <td className={classes.inputTdWithPadding}>
                              <span
                                style={{
                                  color:
                                    jobchart.oldVersions[toCompareIndex] !==
                                      undefined &&
                                    jobchart.oldVersions[toCompareIndex]
                                      .products?.[productIndex]?.styles?.[
                                      styleIndex
                                    ]?.accessories?.[accessoryIndex]?.unit !==
                                      accessory.unit
                                      ? 'Red'
                                      : 'Black',
                                }}
                              >
                                {accessory.unit}
                              </span>
                            </td>
                            <td className={classes.inputTdWithPadding}>
                              <span
                                style={{
                                  color:
                                    jobchart.oldVersions[toCompareIndex] !==
                                      undefined &&
                                    jobchart.oldVersions[toCompareIndex]
                                      .products?.[productIndex]?.styles?.[
                                      styleIndex
                                    ]?.accessories?.[accessoryIndex]
                                      ?.consumption !== accessory.consumption
                                      ? 'Red'
                                      : 'Black',
                                }}
                              >
                                {accessory.forProcess}
                              </span>
                            </td>
                            <td className={classes.inputTdWithPadding}>
                              <span
                                style={{
                                  color:
                                    jobchart.oldVersions[toCompareIndex] !==
                                      undefined &&
                                    jobchart.oldVersions[toCompareIndex]
                                      .products?.[productIndex]?.styles?.[
                                      styleIndex
                                    ]?.accessories?.[accessoryIndex]
                                      ?.forProcess !== accessory.forProcess
                                      ? 'Red'
                                      : 'Black',
                                }}
                              >
                                {accessory.consumption}
                              </span>
                            </td>
                            <td className={classes.inputTdWithPadding}>
                              <span
                                style={{
                                  color:
                                    jobchart.oldVersions[toCompareIndex] !==
                                      undefined &&
                                    jobchart.oldVersions[toCompareIndex]
                                      .products?.[productIndex]?.styles?.[
                                      styleIndex
                                    ]?.accessories?.[accessoryIndex]
                                      ?.additionalPercentage !==
                                      accessory.additionalPercentage
                                      ? 'Red'
                                      : 'Black',
                                }}
                              >
                                {accessory.additionalPercentage}
                              </span>
                            </td>
                            <td className={classes.inputTdWithPadding}>
                              <span
                                style={{
                                  color:
                                    jobchart.oldVersions[toCompareIndex] !==
                                      undefined &&
                                    jobchart.oldVersions[toCompareIndex]
                                      .products?.[productIndex]?.styles?.[
                                      styleIndex
                                    ]?.accessories?.[accessoryIndex]
                                      ?.totalQuantity !==
                                      accessory.totalQuantity
                                      ? 'Red'
                                      : 'Black',
                                }}
                              >
                                {accessory.totalQuantity}
                              </span>
                            </td>
                            <td className={classes.inputTdWithPadding}>
                              <span
                                style={{
                                  color:
                                    jobchart.oldVersions[toCompareIndex] !==
                                      undefined &&
                                    jobchart.oldVersions[toCompareIndex]
                                      .products?.[productIndex]?.styles?.[
                                      styleIndex
                                    ]?.accessories?.[accessoryIndex]?.stock !==
                                      accessory.stock
                                      ? 'Red'
                                      : 'Black',
                                }}
                              >
                                {accessory.stock}
                              </span>
                            </td>
                            <td className={classes.inputTdWithPadding}>
                              <span
                                style={{
                                  color:
                                    jobchart.oldVersions[toCompareIndex] !==
                                      undefined &&
                                    jobchart.oldVersions[toCompareIndex]
                                      .products?.[productIndex]?.styles?.[
                                      styleIndex
                                    ]?.accessories?.[accessoryIndex]
                                      ?.toOrder !== accessory.toOrder
                                      ? 'Red'
                                      : 'Black',
                                }}
                              >
                                {accessory.toOrder}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </Row>
                </>
              ) : null}
            </Fragment>
          );
        })}
        {totalProducts - 1 > productIndex ? <Divider /> : null}
      </Fragment>
    );
  };

  const renderOldVersion = (version, versionIndex) => {
    return (
      <>
        <Collapse key={versionIndex}>
          <Collapse.Panel
            key="1"
            header={`Version ${jobchart.oldVersions.length - versionIndex}`}
            extra={`${
              version.updatedBy
                ? `Updated By ${version.updatedBy}`
                : `Created By ${version.createdBy}`
            } at ${moment(version.updatedAt || version.createdAt).format(
              'DD MMM YYYY'
            )}`}
          >
            {version.products.map((product, productIndex) =>
              renderProduct(
                product,
                productIndex,
                version.products.length,
                versionIndex + 1
              )
            )}
          </Collapse.Panel>
        </Collapse>
        <br />
      </>
    );
  };

  return (
    <>
      <Breadcrumbs
        moduleName="Jobchart"
        moduleLink="/jobcharts/list"
        subModuleName="Preview"
      />
      {jobchart.id ? (
        <>
          <Row>
            <LeftGrid>
              <span className={classes.previewSpan}>Customer: </span>
              {jobchart.customer}
            </LeftGrid>
            <RightGrid>
              <span className={classes.previewSpan}>BPO #: </span>
              {jobchart.bpoNo}
            </RightGrid>
          </Row>
          <br />
          <Row>
            <LeftGrid>
              <span className={classes.previewSpan}>Ship Date: </span>
              {jobchart.shipDate
                ? moment(jobchart.shipDate).format('DD MMM YYYY')
                : null}
            </LeftGrid>
            <RightGrid>
              <span className={classes.previewSpan}>Job Date: </span>
              {jobchart.jobDate
                ? moment(jobchart.jobDate).format('DD MMM YYYY')
                : null}
            </RightGrid>
          </Row>
          <br />
          <Row>
            <LeftGrid>
              <span className={classes.previewSpan}>Remark: </span>
              {jobchart.remark}
            </LeftGrid>
            {permissions.includes(constants.permissions.updateJobchart) ? (
              <RightGrid>
                <Button
                  onClick={() =>
                    history.push(`/jobcharts/edit/${match.params.id}`)
                  }
                  icon={<EditOutlined />}
                >
                  Update
                </Button>
              </RightGrid>
            ) : null}
          </Row>
          <br />
          <Row>
            <Col xs={{ span: 22, offset: 1 }}>
              <Collapse defaultActiveKey={1}>
                <Collapse.Panel
                  key={1}
                  header={`Version ${
                    ((jobchart.oldVersions && jobchart.oldVersions.length) ||
                      0) + 1
                  } (Current version)`}
                  extra={`${
                    jobchart.updatedBy
                      ? `Updated By ${jobchart.updatedBy}`
                      : `Created By ${jobchart.createdBy}`
                  } at ${moment(
                    jobchart.updatedAt || jobchart.createdAt
                  ).format('DD MMM YYYY')}`}
                >
                  {jobchart.products.map((product, index) =>
                    renderProduct(product, index, jobchart.products.length, 0)
                  )}
                </Collapse.Panel>
              </Collapse>
              <br />
              {(jobchart?.oldVersions || []).map(renderOldVersion)}
            </Col>
          </Row>
          <Row>
            <Col xs={{ span: 22, offset: 1 }}>
              {jobchart.stylesSummary && jobchart.stylesSummary.length ? (
                <>
                  <br />
                  <Table
                    bordered
                    size="small"
                    columns={stylesSummaryColumns}
                    dataSource={jobchart.stylesSummary}
                    rowKey={rowKey}
                    pagination={false}
                    title={() => 'Product-Styles Summary'}
                  />
                </>
              ) : null}
              {jobchart.fabricsSummary && jobchart.fabricsSummary.length ? (
                <>
                  <br />
                  <Table
                    bordered
                    size="small"
                    columns={fabricsSummaryColumns}
                    dataSource={jobchart.fabricsSummary}
                    rowKey={rowKey}
                    pagination={false}
                    title={() => 'Product-Fabrics Summary'}
                  />
                </>
              ) : null}
              {jobchart.fabricsSummaryWithoutProduct &&
              jobchart.fabricsSummaryWithoutProduct.length ? (
                <>
                  <br />
                  <Table
                    bordered
                    size="small"
                    columns={fabricsSummaryWithoutProductColumns}
                    dataSource={jobchart.fabricsSummaryWithoutProduct}
                    rowKey={rowKey}
                    pagination={false}
                    title={() => 'Fabrics Summary'}
                  />
                </>
              ) : null}
              {jobchart.accessoriesSummary &&
              jobchart.accessoriesSummary.length ? (
                <>
                  <br />
                  <Table
                    bordered
                    size="small"
                    columns={accessoriesSummaryColumns}
                    dataSource={jobchart.accessoriesSummary}
                    rowKey={rowKey}
                    pagination={false}
                    title={() => 'Accessories Summary'}
                  />
                </>
              ) : null}
            </Col>
          </Row>
        </>
      ) : (
        <Row className={classes.flexCenter}>
          <Spin indicator={antIcon} />
        </Row>
      )}
      <br />
    </>
  );
};

PreviewJobchart.propTypes = {
  classes: PropTypes.object.isRequired,
  match: PropTypes.object.isRequired,
};

export default withStyles(styles)(PreviewJobchart);
